import React, { useEffect, useState, useCallback } from 'react'
import { useTranslate } from 'react-admin'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Box,
  Chip,
  Typography,
  TableSortLabel,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'
import { useAudit, AuditLog } from 'hooks/useAudit'
import UserReference from './user.reference'

export interface FieldConfig {
  field: string           // Название поля в данных
  label: string          // Отображаемое название
  visible?: boolean      // Показывать ли это поле (по умолчанию true)
  formatter?: (value: any, displayValue?: any) => string  // Кастомный форматтер
  extractNamesFromArray?: boolean  // Автоматически извлекать поля name из массива объектов
}

interface AuditTableProps {
  tableName: string
  recordId: string
  className?: string
  showUserColumn?: boolean
  dense?: boolean
  fieldConfigs?: FieldConfig[]  // Конфигурация полей для отображения
  showOnlyConfiguredFields?: boolean  // Показывать только настроенные поля
}

interface ProcessedAuditEntry {
  id: string
  action: string
  user_id: string
  user_name?: string
  user_email?: string
  created_at: string
  changes: Array<{
    field: string
    field_label: string
    old_value: any
    new_value: any
    old_display?: any
    new_display?: any
    field_type: string
    formatter?: (value: any, displayValue?: any) => string
    fieldConfig?: FieldConfig
  }>
}

const AuditTable: React.FC<AuditTableProps> = ({
  tableName,
  recordId,
  className = '',
  showUserColumn = true,
  dense = false,
  fieldConfigs = [],
  showOnlyConfiguredFields
}) => {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([])
  const [processedEntries, setProcessedEntries] = useState<ProcessedAuditEntry[]>([])
  const { fetchHistory, loading, error } = useAudit()
  const translate = useTranslate()

  // Автоматически включаем фильтрацию если есть конфигурация полей
  const shouldShowOnlyConfigured = showOnlyConfiguredFields ?? (fieldConfigs.length > 0)

  const loadAuditHistory = useCallback(async () => {
    if (!tableName || !recordId) return
    
    try {
      const data = await fetchHistory(tableName, recordId)
      setAuditLogs(data || [])
    } catch (error) {
      console.error('Error fetching audit history:', error)
    }
  }, [tableName, recordId, fetchHistory])

  useEffect(() => {
    loadAuditHistory()
  }, [loadAuditHistory])

  // Преобразуем данные: каждая запись аудита = одна строка в таблице
  useEffect(() => {
    const processed: ProcessedAuditEntry[] = auditLogs.map(log => {
      const changes: ProcessedAuditEntry['changes'] = []
      
      // Обрабатываем изменения - они приходят как массив, а не объект
      if (log.changes && Array.isArray(log.changes)) {
        log.changes.forEach((change: any) => {
          const field = change.field
          
          // Ищем все конфигурации для этого поля (включая поля с индексами)
          const matchingConfigs = fieldConfigs.filter(config => {
            // Прямое совпадение
            if (config.field === field) return true
            
            // Проверяем поля с индексами (например, equipment_ids[0] для поля equipment_ids)
            const match = config.field.match(/^(.+)\[(\d+)\]$/)
            if (match) {
              const [, baseField] = match
              return baseField === field
            }
            
            return false
          })
          
          // Если shouldShowOnlyConfigured = true и нет подходящих конфигураций, пропускаем
          if (shouldShowOnlyConfigured && matchingConfigs.length === 0) {
            return
          }
          
          // Если есть конфигурации, добавляем каждую из них
          if (matchingConfigs.length > 0) {
            matchingConfigs.forEach(matchingConfig => {
              // Если поле явно отключено, пропускаем
              if (matchingConfig.visible === false) {
                return
              }
              
              changes.push({
                field: matchingConfig.field,
                field_label: matchingConfig.label || change.field_label || field,
                old_value: change.old_value,
                new_value: change.new_value,
                old_display: change.old_display,
                new_display: change.new_display,
                field_type: change.type || '',
                formatter: matchingConfig.formatter,
                fieldConfig: matchingConfig
              })
            })
          }
        })
      }      
      return {
        id: log.id,
        action: log.action,
        user_id: log.user_id,
        user_name: log.user_name,
        user_email: log.user_email,
        created_at: log.created_at,
        changes
      }
    })
    
    // Сортируем по дате (новые сверху)
    processed.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    
    setProcessedEntries(processed)
  }, [auditLogs, fieldConfigs, shouldShowOnlyConfigured])

  const formatValue = (value: any, displayValue?: any, formatter?: (value: any, displayValue?: any) => string, fieldName?: string, fieldConfig?: FieldConfig): string => {
    // Если есть кастомный форматтер, используем его
    if (formatter) {
      return formatter(value, displayValue)
    }

    // Проверяем опцию извлечения имен из массива
    if (fieldConfig?.extractNamesFromArray) {
      // Если displayValue является массивом объектов с полем name
      if (Array.isArray(displayValue)) {
        return displayValue
          .filter((item: any) => item && item.name)
          .map((item: any) => item.name)
          .join(', ')
      }
      // Если value является массивом объектов с полем name
      if (Array.isArray(value)) {
        return value
          .filter((item: any) => item && item.name)
          .map((item: any) => item.name)
          .join(', ')
      }
    }
    
    // Встроенная логика для полей с индексами (например, equipment_ids[0])
    if (fieldName && fieldName.includes('[')) {
      const match = fieldName.match(/^(.+)\[(\d+)\]$/)

      if (match) {
        const index = parseInt(match[2])
        
        // Для displayValue (массив объектов с name)
        if (Array.isArray(displayValue) && displayValue[index] && displayValue[index].name) {
          return displayValue[index].name
        }
        
        // Для value (массив значений)
        if (Array.isArray(value) && value[index] !== undefined) {
          return String(value[index])
        }
        
        // Для строк в формате PostgreSQL массива {uuid1,uuid2,uuid3}
        if (typeof value === 'string' && value.startsWith('{')) {
          const uuids = value.slice(1, -1).split(',')
          if (uuids[index] !== undefined) {
            const targetUuid = uuids[index]
            
            // Пытаемся найти отображаемое значение для UUID в displayValue
            if (Array.isArray(displayValue)) {
              const foundDisplay = displayValue.find((item: any) => 
                item && typeof item === 'object' && item.id === targetUuid
              )
              if (foundDisplay && foundDisplay.name) {
                return foundDisplay.name
              }
            }
            
            // Если не нашли, возвращаем сырой UUID
            return targetUuid
          }
        }
      }
    }
    
    // Если есть display значение, используем его (для FK и связанных полей)
    if (displayValue !== null && displayValue !== undefined) {
      // Если это объект с name, показываем name
      if (typeof displayValue === 'object' && displayValue.name) {
        return displayValue.name
      }
      // Если это строка, показываем как есть
      if (typeof displayValue === 'string') {
        return displayValue
      }
      
      // В остальных случаях преобразуем в JSON
      return JSON.stringify(displayValue)
    }
    
    // Если нет display значения, обрабатываем само значение
    if (value === null || value === undefined) {
      return '' // Возвращаем пустую строку для null/undefined
    }
    
    if (typeof value === 'boolean') {
      return value ? translate('resources.audit.yes') : translate('resources.audit.no')
    }
    
    // Для массивов пытаемся извлечь простые значения
    if (Array.isArray(value)) {
      return value
        .map(item => {
          if (typeof item === 'object' && item.name) {
            return item.name
          }
          return String(item)
        })
        .join(', ')
    }
    
    // Для объектов пытаемся извлечь name или другие простые поля
    if (typeof value === 'object') {
      if (value.name) {
        return value.name
      }
      // Можно добавить другие стандартные поля
      if (value.title) {
        return value.title
      }
      if (value.label) {
        return value.label
      }
      // В крайнем случае показываем JSON
      return JSON.stringify(value)
    }
    
    return String(value)
  }

  const formatChangeValue = (fieldLabel: string, oldValue: any, newValue: any, oldDisplay?: any, newDisplay?: any, formatter?: (value: any, displayValue?: any) => string, fieldName?: string, fieldConfig?: FieldConfig) => {
    const oldFormatted = formatValue(oldValue, oldDisplay, formatter, fieldName, fieldConfig)
    const newFormatted = formatValue(newValue, newDisplay, formatter, fieldName, fieldConfig)
    console.log(oldFormatted, oldValue, oldDisplay)
    console.log(newFormatted, newValue, newDisplay)
    // Если оба значения пустые, не показываем изменение
    if (!oldFormatted && !newFormatted) {
      return null
    }
    
    // Если значения одинаковые, не показываем изменение
    if (oldFormatted === newFormatted) {
      return null
    }
    
    // Для создания записи (только новое значение)
    if (!oldFormatted && newFormatted) {
      return (
        <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap" sx={{ mb: 0.25 }}>
          <Typography variant="body2" component="span" fontWeight={500}>
            {fieldLabel}:
          </Typography>
          <Box 
            sx={{ 
              px: 0.75, 
              py: 0.25, 
              bgcolor: 'success.50', 
              borderRadius: 1,
              color: 'success.main',
              display: 'inline-block'
            }}
          >
            <Typography variant="body2" component="span">
              {newFormatted}
            </Typography>
          </Box>
        </Box>
      )
    }
    
    // Для удаления записи (только старое значение)
    if (oldFormatted && !newFormatted) {
      return (
        <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap" sx={{ mb: 0.25 }}>
          <Typography variant="body2" component="span" fontWeight={500}>
            {fieldLabel}:
          </Typography>
          <Box 
            sx={{ 
              px: 0.75, 
              py: 0.25, 
              bgcolor: 'error.50', 
              borderRadius: 1,
              color: 'error.main',
              display: 'inline-block'
            }}
          >
            <Typography variant="body2" component="span">
              {oldFormatted}
            </Typography>
          </Box>
        </Box>
      )
    }
    
    // Для изменения (старое → новое)
    return (
      <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap" sx={{ mb: 0.25 }}>
        <Typography variant="body2" component="span" fontWeight={500}>
          {fieldLabel}:
        </Typography>
        <Box 
          sx={{ 
            px: 0.75, 
            py: 0.25, 
            bgcolor: 'error.50', 
            borderRadius: 1,
            color: 'error.main'
          }}
        >
          <Typography variant="body2" component="span">
            {oldFormatted}
          </Typography>
        </Box>
        <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
          →
        </Typography>
        <Box 
          sx={{ 
            px: 0.75, 
            py: 0.25, 
            bgcolor: 'success.50', 
            borderRadius: 1,
            color: 'success.main'
          }}
        >
          <Typography variant="body2" component="span">
            {newFormatted}
          </Typography>
        </Box>
      </Box>
    )
  }

  const renderChanges = (entry: ProcessedAuditEntry) => {
    const { changes, action } = entry
    
    // Если нет изменений, показываем тип операции
    if (!changes || changes.length === 0) {
      let actionText = ''
      switch (action) {
        case 'CREATE':
          actionText = translate('resources.audit.record_created')
          break
        case 'DELETE':
          actionText = translate('resources.audit.record_deleted')
          break
        default:
          actionText = translate('resources.audit.no_changes')
      }
      
      return (
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          {actionText}
        </Typography>
      )
    }

    // Фильтруем и отображаем только реальные изменения
    const renderedChanges = changes
      .map((change, index) => {
        const changeElement = formatChangeValue(
          change.field_label || change.field,
          change.old_value,
          change.new_value,
          change.old_display,
          change.new_display,
          change.formatter,
          change.field,
          change.fieldConfig
        )
        
        // Если изменение не должно отображаться (null), пропускаем его
        if (!changeElement) {
          return null
        }
        
        return changeElement
      })
      .filter(Boolean) // Убираем null элементы

    // Если после фильтрации нет изменений для отображения
    if (renderedChanges.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          {translate('resources.audit.no_visible_changes')}
        </Typography>
      )
    }

    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 0.5  // Уменьшаем расстояние между элементами
        }}
      >
        {renderedChanges}
      </Box>
    )
  }

  const formatDateTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd.MM.yyyy HH:mm', { locale: ru })
    } catch {
      return dateString
    }
  }

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'CREATE':
        return <AddIcon fontSize="small" />
      case 'UPDATE':
        return <EditIcon fontSize="small" />
      case 'DELETE':
        return <DeleteIcon fontSize="small" />
      default:
        return <EditIcon fontSize="small" />
    }
  }

  const getActionColor = (action: string): "primary" | "secondary" | "error" | "warning" | "info" | "success" => {
    switch (action) {
      case 'CREATE':
        return 'success'
      case 'UPDATE':
        return 'primary'
      case 'DELETE':
        return 'error'
      default:
        return 'secondary'
    }
  }

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'CREATE':
        return translate('resources.audit.create')
      case 'UPDATE':
        return translate('resources.audit.update')
      case 'DELETE':
        return translate('resources.audit.delete')
      default:
        return action
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200} className={className}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          {translate('resources.audit.loading')}
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" className={className}>
        {error}
      </Alert>
    )
  }

  if (processedEntries.length === 0) {
    return (
      <Alert severity="info" className={className}>
        {translate('resources.audit.no_history')}
      </Alert>
    )
  }

  return (
    <TableContainer component={Paper} className={className}>
      <Table size={dense ? 'small' : 'medium'}>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel>
                <strong>{translate('resources.audit.action')}</strong>
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <strong>{translate('resources.audit.changes')}</strong>
            </TableCell>
            <TableCell>
              <TableSortLabel>
                <strong>{translate('resources.audit.date_time')}</strong>
              </TableSortLabel>
            </TableCell>
            {showUserColumn && (
              <TableCell>
                <strong>{translate('resources.audit.user')}</strong>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {processedEntries.map((entry) => (
            <TableRow key={entry.id} hover>
              <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  {getActionIcon(entry.action)}
                  <Chip
                    label={getActionLabel(entry.action)}
                    color={getActionColor(entry.action)}
                    size="small"
                  />
                </Box>
              </TableCell>
              <TableCell>
                {renderChanges(entry)}
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatDateTime(entry.created_at)}
                </Typography>
              </TableCell>
              {showUserColumn && (
                <TableCell>
                  <UserReference 
                    userId={entry.user_id} 
                    fallback={entry.user_name || entry.user_email || translate('resources.audit.system')} 
                  />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default AuditTable
